import React, { useState, useMemo, useEffect, useRef } from 'react';
import './UserSearch.scss';
import classNames from 'classnames';
import { UserLocation } from '@hone-automation/common';

export interface UserSearchType extends UserLocation {
  isOnline: boolean;
  name: string;
}

export interface UserSearchProps {
  users: UserLocation[] | undefined;
  placeholder?: string;
  onUserSelect?: (user: UserSearchType) => void;
  onClose?: () => void;
  className?: string;
  isOpen?: boolean;
  searchText?: string;
  getFilteredUsers?: (users: UserSearchType[]) => void;
}

const UserSearch: React.FC<UserSearchProps> = ({
  users,
  placeholder = 'Search',
  onUserSelect,
  onClose,
  className = '',
  isOpen = false,
  searchText = '',
  getFilteredUsers,
}) => {
  const initialUsers = useMemo(() => {
    if (users) {
      return users as UserSearchType[];
    }
  }, [users]);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const userListRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredUsers = useMemo(() => {
    // Filter out users without names to prevent blank entries
    const validUsers = initialUsers?.filter((user: UserSearchType) => user.name && user.name.trim() !== '');

    if (!searchTerm.trim()) return validUsers;
    const filteredUsers =
      validUsers &&
      validUsers.filter(
        (user: UserSearchType) => user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    if (getFilteredUsers && filteredUsers) {
      getFilteredUsers(filteredUsers);
    }
    return filteredUsers;
  }, [initialUsers, searchTerm]);

  const handleUserClick = (user: UserSearchType): void => {
    if (onUserSelect) {
      onUserSelect(user);
    }
    // Reset selected index after selection
    setSelectedIndex(0);
  };

  // Reset selected index when search term or filtered users change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm, filteredUsers?.length]);

  useEffect(() => {
    setSearchTerm(searchText);
  }, [searchText]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Use a small delay to ensure the dropdown is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current && isOpen) {
      selectedItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex, isOpen]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!filteredUsers || filteredUsers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < filteredUsers.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredUsers[selectedIndex]) {
          handleUserClick(filteredUsers[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose?.();
        break;
    }
  };

  const handleKeyDown =
    (user: UserSearchType) =>
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleUserClick(user);
      }
    };

  const initials = (name: string): string => {
    if (!name) return '';
    return name
      .trim()
      .split(' ')
      .filter(word => word.length > 0)
      .map(word => word[0].toUpperCase())
      .filter((char, index) => index < 2)
      .join('');
  };

  return (
    <div className={classNames('user-search', className, { 'user-search--open': isOpen })}>
      <div className="search-container">
        <div className="search-icon">
          <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              opacity="0.5"
              d="M10.4406 10.3496L15.2819 14.4786M9.96015 9.94208C8.14426 12.0942 4.93651 12.3576 2.79544 10.5303C0.654367 8.70304 0.390759 5.47708 2.20665 3.32494C4.02254 1.17279 7.2303 0.909431 9.37137 2.73671C11.5124 4.56398 11.776 7.78994 9.96015 9.94208Z"
              stroke="black"
              strokeWidth="1.53367"
              strokeLinecap="square"
            />
          </svg>
        </div>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          className="search-input"
        />
      </div>

      <div className="user-list" ref={userListRef}>
        {filteredUsers &&
          filteredUsers.map((user, index) => (
            <div
              key={user.userId}
              ref={index === selectedIndex ? selectedItemRef : null}
              className={classNames('user-item', {
                online: user.isOnline,
                offline: !user.isOnline,
                selected: index === selectedIndex,
              })}
              onClick={() => handleUserClick(user)}
              role="button"
              onKeyDown={handleKeyDown(user)}
              tabIndex={0}
              aria-label={`Select ${user.name} - ${user.isOnline ? 'Online' : 'Offline'}`}
            >
              <div className="status-indicator" />
              <div className="user-avatar">
                <span className="user-initials">{initials(user.name)}</span>
              </div>
              <span className="user-name">{user.name}</span>
            </div>
          ))}

        {filteredUsers && filteredUsers.length === 0 && searchTerm && (
          <div className="no-results">No users found matching "{searchTerm}"</div>
        )}
      </div>
    </div>
  );
};

export default UserSearch;
